import { Component, OnDestroy, OnInit } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { DataService } from 'src/app/services/data.service';
import { TopicButtonComponent } from 'src/app/shared/components/topic-button/topic-button.component';
import { Observable, Subscription, map, of } from 'rxjs';
import {
  AdditionalComments,
  Topic,
  FormData,
  EventData,
} from 'src/app/services/data.model';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { format, parse, parseISO } from 'date-fns';
import { NavigationService } from 'src/app/services/navigation.service';
import { TOPICS_LIST } from 'src/app/shared/constants/constants';

@Component({
  selector: 'app-additional-comments',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MainLayoutComponent,
    FormGroupComponent,
    TopicButtonComponent
],
  templateUrl: './additional-comments.component.html',
  styleUrl: './additional-comments.component.scss',
})
export class AdditionalCommentsComponent implements OnInit, OnDestroy {
  additionalCommentsForm: FormGroup;
  topics: Topic[] = TOPICS_LIST.sort(() => Math.random() - 0.5);
  subscriptions: Subscription = new Subscription();
  previouslyFilledData: AdditionalComments = {
    comments: {
      comments: '',
    },
    topics: [],
  };
  subtitles = {
    1: 'Your appointment is booked!',
    2: 'We will call to setup your appointment time.',
  };

  hasDate = false;
  appointmentDate: any;
  isEvent = false;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    public nextButtonState: NextButtonStateService,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.checkEventFlow();
    if (this.dataService.id) {
      const sub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          this.prepopulateStepData(data);
          const date =
            data.appointmentRequest?.appointment?.appointmentDateTime;
          this.hasDate = !!date;
          if (this.hasDate) {
            const sessionData = JSON.parse(data.sessionData);
            if (sessionData.appointmentInfo) {
              const time = format(
                parse(
                  sessionData.appointmentInfo.selectedStartTime,
                  'HH:mm:ss',
                  new Date()
                ),
                'h:mma'
              );
              this.appointmentDate =
                format(parseISO(date), 'EEEE, MMMM do @ ') +
                time?.toLowerCase();
            }
          }
        });
      this.subscriptions.add(sub);
    }
  }

  checkEventFlow() {
    const eventSub = this.navigationService
      .getEventData()
      .subscribe((event: EventData) => {
        this.isEvent = !!event?.isEvent;
      });
    this.subscriptions.add(eventSub);
  }

  buildForm() {
    this.additionalCommentsForm = this.fb.group({
      comments: ['', [Validators.maxLength(3000)]],
    });
    this.subscribeToFormChanges();
  }

  prepopulateStepData(data: FormData) {
    if (data?.appointmentRequest?.customerComments.length) {
      this.previouslyFilledData.comments.comments =
        data.appointmentRequest.customerComments;
      const sesssionData = JSON.parse(data.sessionData);
      this.previouslyFilledData.topics = sesssionData.topics;
      this.updateFormValues();
    }
    this.checkFormValidity();
  }

  subscribeToFormChanges(): void {
    const additionalCommentsFormSub =
      this.additionalCommentsForm.valueChanges.subscribe((value) => {
        this.checkFormValidity();
      });
    this.subscriptions.add(additionalCommentsFormSub);
  }

  checkFormValidity() {
    setTimeout((_) => {
      this.nextButtonState.emitActivateNext(this.additionalCommentsForm.valid);
    });
  }

  onClickNext = (): Observable<any> => {
    //comments-submitted
    if (this.additionalCommentsForm.valid) {
      return this.isEvent
        ? this.saveData(PAGES.COMMENTS_SUBMITTED)
        : this.saveData(
            NAVIGATION_MAP[PAGES.ADDITIONAL_COMMENTS].nextButton.route
          );
    }
    this.additionalCommentsForm.markAllAsTouched();
    return of(false);
  };

  onClickBack = (): Observable<any> => {
    return this.additionalCommentsForm.valid
      ? this.saveData(
          NAVIGATION_MAP[PAGES.ADDITIONAL_COMMENTS].backButton.route
        )
      : of(true);
  };

  saveData(nextPage): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };

    stepsData.appointmentRequest.customerComments =
      this.additionalCommentsForm.value.comments;
    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage;
    sessionData.topics = this.topics.filter((item: Topic) => item.selected);
    stepsData.sessionData = JSON.stringify(sessionData);
    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        return nextPage;
      })
    );
  }

  updateFormValues() {
    this.additionalCommentsForm.patchValue({
      comments: this.previouslyFilledData.comments.comments,
    });
    this.topics = this.topics.map((topic: Topic) => {
      if (
        this.previouslyFilledData.topics.filter(
          (previouslySelected) =>
            topic.unique_name === previouslySelected.unique_name
        ).length > 0
      ) {
        return { ...topic, selected: true };
      } else {
        return topic;
      }
    });
  }

  onSelectTopic(item): void {
    const selectedItems = this.topics.filter((item) => item.selected).length;

    this.topics = this.topics.map((topic) => {
      if (item.unique_name === topic.unique_name) {
        if (selectedItems === 3 && !item.selected) {
          return topic;
        }
        return { ...topic, selected: !item.selected };
      } else {
        return topic;
      }
    });
  }

  identify(index: number, item: { unique_name: string }): string {
    return item.unique_name;
  }

  ngOnDestroy(): void {
    if (this.subscriptions) this.subscriptions.unsubscribe();
  }
}
